import { isGoldItemType } from './goldItemTypes.mjs'

const OPTIONAL_ITEM_FIELDS = ['description', 'manufacturer', 'model', 'serialNumber', 'inscription']
const TOTAL_FIELDS = ['totalWeight', 'totalPureWeight', 'totalGross', 'totalDesagio', 'totalNet']
const MAX_SAFE_CENTS = BigInt(Number.MAX_SAFE_INTEGER)

function invalid(reason) {
  throw new Error(`Cannot save purchase: ${reason}.`)
}

function itemIdentity(item) {
  const value = item?.itemId ?? item?.id
  if ((typeof value !== 'string' && typeof value !== 'number') ||
      (typeof value === 'number' && !Number.isSafeInteger(value))) {
    invalid('each item needs a stable ID')
  }
  const id = String(value).trim()
  if (!id) invalid('each item needs a stable ID')
  return id
}

function optionalText(value) {
  if (value == null) return null
  if (typeof value !== 'string') invalid('item details must be text')
  return value.trim() || null
}

function nonnegativeNumber(value) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    invalid('calculated amounts must be finite nonnegative numbers')
  }
  return value
}

function numericInput(value) {
  if ((typeof value !== 'string' && typeof value !== 'number') ||
      (typeof value === 'string' && !value.trim())) {
    invalid('item measurements are invalid')
  }
  // Number, unlike parseFloat, rejects partially numeric input such as "2grams".
  const number = Number(value)
  if (!Number.isFinite(number)) invalid('item measurements are invalid')
  return number
}

function sameAmount(left, right, count = 1) {
  const scale = Math.max(1, Math.abs(left), Math.abs(right))
  // Permit arithmetic noise, never a material fraction of a cent.
  const tolerance = Math.max(1e-9, Math.min(1e-6, Number.EPSILON * scale * (count + 1) * 8))
  return Math.abs(left - right) <= tolerance
}

function itemShape(item, index) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) invalid('item data is missing')
  if (item.metal !== 'gold' && item.metal !== 'silver') invalid('item metal is invalid')
  const weight = numericInput(item.weight)
  if (weight <= 0) invalid('item weight must be positive')
  const itemType = item.itemType
  if (!isGoldItemType(itemType)) invalid(`item ${index + 1} needs a recognized item type`)
  const karatWhole = item.karatWhole == null || item.karatWhole === '' ? '' : numericInput(item.karatWhole)
  const karatDecimal = item.karatDecimal == null || item.karatDecimal === '' ? '' : numericInput(item.karatDecimal)
  if (item.metal === 'gold' &&
      (!Number.isInteger(karatWhole) || !Number.isInteger(karatDecimal) ||
       karatWhole < 1 || karatWhole > 24 || karatDecimal < 0 || karatDecimal > 99 ||
       karatWhole + karatDecimal / 100 > 24)) {
    invalid('gold purity is invalid')
  }
  return { metal: item.metal, karatWhole, karatDecimal, weight, itemType }
}

// Read the confirmed number's decimal representation, including scientific
// notation. This avoids binary multiplication rounding 1.005 dollars to 100 cents.
function centsParts(value) {
  nonnegativeNumber(value)
  const [coefficient, exponentText = '0'] = String(value).toLowerCase().split('e')
  const [whole, decimal = ''] = coefficient.split('.')
  const digits = BigInt(whole + decimal)
  const exponent = Number(exponentText) + 2 - decimal.length
  let cents
  let remainder = 0n
  let denominator = 1n
  if (exponent >= 0) {
    cents = digits * 10n ** BigInt(exponent)
  } else {
    denominator = 10n ** BigInt(-exponent)
    cents = digits / denominator
    remainder = digits % denominator
  }
  if (cents > MAX_SAFE_CENTS) invalid('payout exceeds safe cent precision')
  return { cents, remainder, denominator }
}

function roundedCents(value) {
  const { cents, remainder, denominator } = centsParts(value)
  const rounded = cents + (remainder * 2n >= denominator ? 1n : 0n)
  if (rounded > MAX_SAFE_CENTS) invalid('payout exceeds safe cent precision')
  return rounded
}

/**
 * Persist a confirmed purchase without consulting prices or recomputing payouts.
 * IDs are scoped to the transaction; output order follows the original item rows.
 * Reconciled payoutCents is authoritative; totalNetConfirmed retains the source sum.
 */
export function buildGoldPurchaseSnapshot({ items, calculations } = {}) {
  if (!Array.isArray(items) || !items.length || !calculations ||
      !Array.isArray(calculations.items) || !calculations.items.length) {
    invalid('confirmed item calculations are missing')
  }

  const calculatedById = new Map()
  for (const calculated of calculations.items) {
    const id = itemIdentity(calculated)
    if (calculatedById.has(id)) invalid('calculated item IDs must be unique')
    calculatedById.set(id, calculated)
  }

  const ids = new Set()
  const totals = Object.fromEntries(TOTAL_FIELDS.map(field => [field, nonnegativeNumber(calculations[field])]))
  const sums = Object.fromEntries(TOTAL_FIELDS.map(field => [field, 0]))
  const entries = []
  for (const [index, item] of items.entries()) {
    const itemId = itemIdentity(item)
    if (ids.has(itemId)) invalid('item IDs must be unique')
    ids.add(itemId)
    const calculated = calculatedById.get(itemId)
    const emptyWeight = item.weight == null || (typeof item.weight === 'string' && !item.weight.trim())
    if (emptyWeight && !calculated) continue

    const shape = itemShape(item, index)
    if (!calculated) invalid('an item has no confirmed calculation')
    const calculatedShape = itemShape(calculated, index)
    if (Object.keys(shape).some(field => shape[field] !== calculatedShape[field])) {
      invalid('item details differ from the confirmed calculation')
    }

    const netValue = nonnegativeNumber(calculated.netValue)
    const grossValue = nonnegativeNumber(calculated.grossValue)
    const desagioAmount = nonnegativeNumber(calculated.desagioAmount)
    const pureWeight = nonnegativeNumber(calculated.pureWeight)
    const purity = nonnegativeNumber(calculated.purity)
    const desagio = nonnegativeNumber(calculated.desagio)
    nonnegativeNumber(calculated.basePrice)
    if (purity > 1 || desagio > 100 || pureWeight > shape.weight ||
        !sameAmount(grossValue - desagioAmount, netValue)) {
      invalid('item financial calculations are inconsistent')
    }

    sums.totalWeight += shape.weight
    sums.totalPureWeight += pureWeight
    sums.totalGross += grossValue
    sums.totalDesagio += desagioAmount
    sums.totalNet += netValue

    const snapshot = {
      itemId,
      metal: shape.metal,
      karatWhole: item.karatWhole ?? '',
      karatDecimal: item.karatDecimal ?? '',
      weight: shape.weight,
      itemType: shape.itemType,
      ...Object.fromEntries(OPTIONAL_ITEM_FIELDS.map(field => [field, optionalText(item[field])])),
    }
    entries.push({ itemId, snapshot, ...centsParts(netValue) })
    calculatedById.delete(itemId)
  }

  if (!entries.length || calculatedById.size) invalid('confirmed calculations do not match the item rows')
  for (const field of TOTAL_FIELDS) {
    if (!Number.isFinite(sums[field]) || !sameAmount(sums[field], totals[field], entries.length)) {
      invalid('confirmed totals do not match the item calculations')
    }
  }

  const payoutCents = roundedCents(totals.totalNet)
  const floorTotal = entries.reduce((sum, entry) => sum + entry.cents, 0n)
  const remaining = payoutCents - floorTotal
  if (remaining < 0n || remaining > BigInt(entries.length)) invalid('payout cents cannot be reconciled')

  const byRemainder = [...entries].sort((left, right) => {
    const difference = left.remainder * right.denominator - right.remainder * left.denominator
    if (difference !== 0n) return difference > 0n ? -1 : 1
    return left.itemId < right.itemId ? -1 : left.itemId > right.itemId ? 1 : 0
  })
  for (let index = 0; index < Number(remaining); index++) byRemainder[index].cents += 1n

  return {
    items: entries.map(entry => {
      if (entry.cents > MAX_SAFE_CENTS) invalid('payout exceeds safe cent precision')
      const cents = Number(entry.cents)
      return { ...entry.snapshot, payoutCents: cents, netValue: cents / 100 }
    }),
    totals: {
      ...totals,
      totalNetConfirmed: totals.totalNet,
      totalNet: Number(payoutCents) / 100,
      payoutCents: Number(payoutCents),
    },
  }
}

function isGregorianDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const [year, month, day] = value.split('-').map(Number)
  if (year < 1 || month < 1 || month > 12 || day < 1) return false
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
  const monthDays = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  return day <= monthDays[month - 1]
}

/** Empty DOB is allowed. Both dates use the caller's local YYYY-MM-DD calendar. */
export function validateOptionalDateOfBirth(value, today) {
  if (value == null || (typeof value === 'string' && !value.trim())) return null
  if (!isGregorianDate(value)) return 'Enter a valid date of birth (YYYY-MM-DD).'
  if (!isGregorianDate(today)) return 'The current date is invalid.'
  if (value > today) return 'Date of birth cannot be in the future.'
  return null
}

export function localDateKey(date) {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime()) || date.getFullYear() < 1 || date.getFullYear() > 9999) {
    throw new Error('A valid local date is required.')
  }
  return `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

/** Classify supported capture data URLs without logging or decoding image data. */
export function getCaptureMediaInfo(dataUrl) {
  const error = 'Capture must be a base64 JPEG or PNG image.'
  if (typeof dataUrl !== 'string') throw new Error(error)
  const comma = dataUrl.indexOf(',')
  const header = dataUrl.slice(0, comma).match(/^data:image\/(jpeg|png);base64$/i)
  const payload = comma < 0 ? '' : dataUrl.slice(comma + 1)
  if (!header || !payload || payload.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(payload)) {
    throw new Error(error)
  }
  const format = header[1].toLowerCase()
  return { mimeType: `image/${format}`, extension: format === 'jpeg' ? 'jpg' : 'png' }
}
